import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import type {
  LiveEventAdmin,
  LiveEventGameMode,
  LiveEventGameSessionAdmin,
  LiveEventSessionStatus,
} from 'api/generated'
import { Header } from 'components/layout/header'
import { Main } from 'components/layout/main'
import { Search } from 'components/search'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Checkbox } from 'components/ui/checkbox'
import { Label } from 'components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select'
import { Switch } from 'components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs'
import { GenericDataTable } from 'helpers/generic-data-table'
import {
  copyToClipboard,
  liveEventHomeUrl,
  liveEventQrUrl,
  liveEventScreenUrl,
  MODE_LABELS,
  STATUS_LABELS,
} from 'helpers/live-events'
import {
  liveEventLeaderboardColumns,
  liveEventSessionsColumns,
} from 'helpers/live-events-columns'
import { Copy, ExternalLink, Loader, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import {
  deleteLiveEventSession,
  fetchLiveEvent,
  fetchLiveEventLeaderboard,
  fetchLiveEventQueue,
  fetchLiveEventSessions,
  invalidateLiveEventSession,
  restoreLiveEventSession,
  setLiveEventScreen,
} from 'store/liveEvents/liveEventsSlice'
import { useAppDispatch, useAppSelector } from 'store/store'
import type { LiveEventSessionsFilters } from 'types/liveEvents'
import LiveEventQrCard from 'ui/components/LiveEventQrCard'

const QUEUE_REFRESH_MS = 5000
const ALL = 'all'

const LinkRow = ({
  label,
  url,
  hint,
}: {
  label: string
  url: string
  hint: string
}) => (
  <div className='flex flex-col gap-1'>
    <span className='text-sm font-medium'>{label}</span>
    <div className='flex items-center gap-2'>
      <code className='flex-1 truncate rounded bg-muted px-2 py-1 text-xs'>
        {url}
      </code>
      <Button
        size='icon'
        variant='outline'
        aria-label={`Copia ${label}`}
        onClick={() =>
          copyToClipboard(url).then(() => toast.success('Link copiato'))
        }
      >
        <Copy className='h-4 w-4' />
      </Button>
      <Button
        size='icon'
        variant='outline'
        aria-label={`Apri ${label}`}
        asChild
      >
        <a href={url} target='_blank' rel='noopener noreferrer'>
          <ExternalLink className='h-4 w-4' />
        </a>
      </Button>
    </div>
    <span className='text-xs text-muted-foreground'>{hint}</span>
  </div>
)

const LinksCard = ({ event }: { event: LiveEventAdmin }) => (
  <Card>
    <CardHeader>
      <CardTitle>Link</CardTitle>
    </CardHeader>
    <CardContent className='flex flex-col gap-4'>
      <LinkRow
        label='QR ufficiale (partite live)'
        url={liveEventQrUrl(event)}
        hint="Da stampare come QR: apre l'app se installata, altrimenti la pagina per scaricarla. Non cambia mai (a meno di cambiare slug o chiave)."
      />
      <LinkRow
        label='Da app (partite da casa)'
        url={liveEventHomeUrl(event)}
        hint='Per banner, push o link in app: classifica secondaria.'
      />
      <LinkRow
        label='Maxischermo'
        url={liveEventScreenUrl(event)}
        hint='Da aprire a tutto schermo sul PC collegato allo schermo, loggati con un account di backoffice.'
      />
    </CardContent>
  </Card>
)

const QueueTab = ({ event }: { event: LiveEventAdmin }) => {
  const dispatch = useAppDispatch()
  const queue = useAppSelector((s) => s.liveEvents.queue)

  useEffect(() => {
    dispatch(fetchLiveEventQueue(event.id))
    const timer = window.setInterval(
      () => dispatch(fetchLiveEventQueue(event.id)),
      QUEUE_REFRESH_MS
    )
    return () => window.clearInterval(timer)
  }, [dispatch, event.id])

  const remove = (sessionId: number, name: string) => {
    if (!window.confirm(`Togliere ${name} dalla coda?`)) return
    dispatch(deleteLiveEventSession({ id: event.id, sessionId })).then(() =>
      dispatch(fetchLiveEventQueue(event.id))
    )
  }

  if (!queue) return <Loader className='h-5 w-5 animate-spin' />

  return (
    <div className='grid gap-4 lg:grid-cols-2'>
      <Card>
        <CardHeader>
          <CardTitle>Sul maxischermo</CardTitle>
        </CardHeader>
        <CardContent>
          {!queue.screen_enabled ? (
            <p className='text-sm text-muted-foreground'>
              Maxischermo spento: le partite live partono subito, senza coda.
            </p>
          ) : queue.current ? (
            <div className='flex items-center justify-between gap-3'>
              <div className='flex flex-col'>
                <span className='font-medium'>
                  {queue.current.user?.name ?? '—'}
                </span>
                <span className='text-sm text-muted-foreground'>
                  {STATUS_LABELS[queue.current.status]}
                  {queue.current.score != null &&
                    ` · ${queue.current.score} punti`}
                </span>
              </div>
              <Button
                size='sm'
                variant='destructive'
                onClick={() =>
                  remove(
                    queue.current!.id,
                    queue.current!.user?.name ?? 'il giocatore'
                  )
                }
              >
                Togli dallo schermo
              </Button>
            </div>
          ) : (
            <p className='text-sm text-muted-foreground'>
              Nessuno sta giocando: lo schermo mostra classifica e QR.
            </p>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>In coda ({queue.queue_length})</CardTitle>
        </CardHeader>
        <CardContent>
          {queue.queue.length === 0 ? (
            <p className='text-sm text-muted-foreground'>Nessuno in coda.</p>
          ) : (
            <ol className='flex flex-col gap-2'>
              {queue.queue.map((entry) => (
                <li
                  key={entry.session_id}
                  className='flex items-center justify-between gap-3'
                >
                  <span>
                    <span className='mr-2 text-muted-foreground tabular-nums'>
                      {entry.position}.
                    </span>
                    {entry.user?.name ?? '—'}
                  </span>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() =>
                      remove(
                        entry.session_id,
                        entry.user?.name ?? 'il giocatore'
                      )
                    }
                  >
                    Togli
                  </Button>
                </li>
              ))}
              {queue.queue_length > queue.queue.length && (
                <li className='text-sm text-muted-foreground'>
                  …e altri {queue.queue_length - queue.queue.length}
                </li>
              )}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

const SessionsTab = ({ event }: { event: LiveEventAdmin }) => {
  const dispatch = useAppDispatch()
  const { sessions, sessionsTotalPages, sessionsLoading } = useAppSelector(
    (s) => s.liveEvents
  )
  const [pageIndex, setPageIndex] = useState(0)
  const [filters, setFilters] = useState<LiveEventSessionsFilters>({})

  const load = useCallback(() => {
    dispatch(
      fetchLiveEventSessions({ id: event.id, page: pageIndex + 1, filters })
    )
  }, [dispatch, event.id, pageIndex, filters])

  useEffect(load, [load])

  const updateFilter = (patch: LiveEventSessionsFilters) => {
    setPageIndex(0)
    setFilters((prev) => ({ ...prev, ...patch }))
  }

  const toolbar = (
    <div className='flex flex-wrap items-center gap-4'>
      <Select
        value={filters.mode ?? ALL}
        onValueChange={(v) =>
          updateFilter({
            mode: v === ALL ? undefined : (v as LiveEventGameMode),
          })
        }
      >
        <SelectTrigger className='w-40'>
          <SelectValue placeholder='Modalità' />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Tutte le modalità</SelectItem>
          {Object.entries(MODE_LABELS).map(([value, label]) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={filters.status ?? ALL}
        onValueChange={(v) =>
          updateFilter({
            status: v === ALL ? undefined : (v as LiveEventSessionStatus),
          })
        }
      >
        <SelectTrigger className='w-40'>
          <SelectValue placeholder='Stato' />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Tutti gli stati</SelectItem>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {(
        [
          ['suspicious', 'Solo sospette'],
          ['invalidated', 'Solo annullate'],
          ['with_deleted', 'Includi eliminate'],
        ] as const
      ).map(([key, label]) => (
        <label key={key} className='flex items-center gap-2 text-sm'>
          <Checkbox
            checked={filters[key] ?? false}
            onCheckedChange={(checked) =>
              updateFilter({ [key]: checked === true ? true : undefined })
            }
          />
          {label}
        </label>
      ))}
      <Button size='sm' variant='outline' onClick={load}>
        <RefreshCw className='mr-1 h-4 w-4' /> Aggiorna
      </Button>
      {sessionsLoading && <Loader className='h-4 w-4 animate-spin' />}
    </div>
  )

  const withSession =
    (thunk: typeof restoreLiveEventSession | typeof deleteLiveEventSession) =>
    (session: LiveEventGameSessionAdmin) =>
      dispatch(thunk({ id: event.id, sessionId: session.id }))

  return (
    <GenericDataTable
      columns={liveEventSessionsColumns({
        onInvalidate: (session) => {
          const reason = window.prompt(
            `Annullare la partita di ${session.user.name}? Motivo (facoltativo):`
          )
          if (reason === null) return
          dispatch(
            invalidateLiveEventSession({
              id: event.id,
              sessionId: session.id,
              reason: reason || undefined,
            })
          )
        },
        onRestore: withSession(restoreLiveEventSession),
        onDelete: withSession(deleteLiveEventSession),
      })}
      data={sessions}
      pageCount={sessionsTotalPages}
      pageIndex={pageIndex}
      pageSize={50}
      onPageIndexChange={setPageIndex}
      toolbar={toolbar}
    />
  )
}

const LeaderboardTab = ({ event }: { event: LiveEventAdmin }) => {
  const dispatch = useAppDispatch()
  const leaderboard = useAppSelector((s) => s.liveEvents.leaderboard)
  const [mode, setMode] = useState<LiveEventGameMode>('live')

  useEffect(() => {
    dispatch(fetchLiveEventLeaderboard({ id: event.id, mode }))
  }, [dispatch, event.id, mode])

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center gap-3'>
        <Tabs
          value={mode}
          onValueChange={(v) => setMode(v as LiveEventGameMode)}
        >
          <TabsList>
            <TabsTrigger value='live'>{MODE_LABELS.live}</TabsTrigger>
            <TabsTrigger value='home'>{MODE_LABELS.home}</TabsTrigger>
          </TabsList>
        </Tabs>
        {leaderboard?.mode === mode && (
          <span className='text-sm text-muted-foreground'>
            {leaderboard.players_count} giocatori
          </span>
        )}
      </div>
      {leaderboard?.mode === mode ? (
        <GenericDataTable
          columns={liveEventLeaderboardColumns}
          data={leaderboard.data}
          pageCount={1}
          pageIndex={0}
          pageSize={leaderboard.data.length || 1}
          onPageIndexChange={() => {}}
        />
      ) : (
        <Loader className='h-5 w-5 animate-spin' />
      )}
      <p className='text-xs text-muted-foreground'>
        Miglior punteggio valido per giocatore; a parità vince chi l&apos;ha
        fatto prima. Le partite annullate o eliminate non contano.
      </p>
    </div>
  )
}

const LiveEventDetailPage = () => {
  const dispatch = useAppDispatch()
  const { id } = useParams({ from: '/protected/eventi-live/$id' }) as {
    id: string
  }
  const eventId = parseInt(id, 10)
  const { event, loading, error } = useAppSelector((s) => s.liveEvents)

  useEffect(() => {
    if (!isNaN(eventId)) dispatch(fetchLiveEvent(eventId))
  }, [dispatch, eventId])

  return (
    <>
      <Header>
        <div className='ml-auto flex items-center space-x-4'>
          <Search />
        </div>
      </Header>
      <Main>
        {!event || event.id !== eventId ? (
          loading ? (
            <Loader className='h-5 w-5 animate-spin' />
          ) : (
            <p className='text-muted-foreground'>
              {error ?? 'Evento non trovato.'}
            </p>
          )
        ) : (
          <div className='flex flex-col gap-6'>
            <div className='flex flex-wrap items-start justify-between gap-4'>
              <div>
                <div className='flex items-center gap-2'>
                  <h2 className='text-2xl font-bold tracking-tight'>
                    {event.name}
                  </h2>
                  {!event.active ? (
                    <Badge variant='outline'>Non attivo</Badge>
                  ) : event.open ? (
                    <Badge>Aperto</Badge>
                  ) : (
                    <Badge variant='secondary'>Fuori orario</Badge>
                  )}
                </div>
                <p className='text-muted-foreground'>
                  {event.brand_name ?? 'Nessun brand'} · /evento/{event.slug}
                </p>
              </div>
              <div className='flex items-center gap-4'>
                <div className='flex items-center gap-2'>
                  <Switch
                    id='le-screen'
                    checked={event.screen_enabled}
                    onCheckedChange={(enabled) =>
                      dispatch(setLiveEventScreen({ id: event.id, enabled }))
                    }
                  />
                  <Label htmlFor='le-screen'>Maxischermo acceso</Label>
                </div>
                <Button variant='outline' asChild>
                  <Link
                    to='/eventi-live/$id/modifica'
                    params={{ id: event.id.toString() }}
                  >
                    Modifica
                  </Link>
                </Button>
              </div>
            </div>

            <Tabs defaultValue='queue'>
              <TabsList>
                <TabsTrigger value='queue'>Coda</TabsTrigger>
                <TabsTrigger value='sessions'>Partite</TabsTrigger>
                <TabsTrigger value='leaderboard'>Classifica</TabsTrigger>
                <TabsTrigger value='links'>Link e QR</TabsTrigger>
              </TabsList>
              <TabsContent value='queue' className='pt-4'>
                <QueueTab event={event} />
              </TabsContent>
              <TabsContent value='sessions' className='pt-4'>
                <SessionsTab event={event} />
              </TabsContent>
              <TabsContent value='leaderboard' className='pt-4'>
                <LeaderboardTab event={event} />
              </TabsContent>
              <TabsContent value='links' className='pt-4'>
                <div className='flex flex-col gap-4'>
                  <LiveEventQrCard event={event} />
                  <LinksCard event={event} />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </Main>
    </>
  )
}

export default LiveEventDetailPage
